import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Check, Music, Play, RotateCcw, Upload, X } from 'lucide-react'
import { useUploadSound } from './hooks/useUploadSound'

export default function NotificationSoundSetting() {
	const {
		isLoading,
		isUploading,
		currentSoundUrl,
		selectedFile,
		fileInputRef,
		handleFileSelect,
		handlePreview,
		handleUpload,
		handleCancelSelection,
		handleReset,
		handleUploadClick,
	} = useUploadSound()

	const currentFileName = currentSoundUrl
		? currentSoundUrl.split('/').pop()
		: null

	return (
		<Card>
			<CardHeader>
				<CardTitle>เสียงแจ้งเตือนรายการถอน/ฝาก</CardTitle>
				<CardDescription>
					ปรับแต่งเสียงแจ้งเตือนเมื่อมีรายการถอนหรือฝากเงินรอดำเนินการ
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{isLoading ? (
					<p className="text-sm text-muted-foreground">กำลังโหลด...</p>
				) : (
					<>
						{/* Current sound info */}
						<div className="flex items-center gap-3 rounded-lg border bg-muted/50 p-3">
							<Music className="h-5 w-5 shrink-0 text-muted-foreground" />
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium">เสียงปัจจุบัน</p>
								<p className="truncate text-xs text-muted-foreground">
									{currentFileName ?? 'ใช้เสียงเริ่มต้น'}
								</p>
							</div>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => handlePreview()}
								disabled={isUploading}
							>
								<Play className="h-4 w-4" />
								<span className="ml-1">ฟัง</span>
							</Button>
						</div>

						{/* Selected file preview */}
						{selectedFile && (
							<div className="flex items-center gap-3 rounded-lg border-2 border-primary/50 bg-muted/50 p-3">
								<Music className="h-5 w-5 shrink-0 text-primary" />
								<div className="min-w-0 flex-1">
									<p className="text-sm font-medium text-primary">
										ไฟล์ที่เลือก
									</p>
									<p className="truncate text-xs text-muted-foreground">
										{selectedFile.name}
									</p>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() =>
										handlePreview(URL.createObjectURL(selectedFile))
									}
									disabled={isUploading}
								>
									<Play className="h-4 w-4" />
									<span className="ml-1">ฟัง</span>
								</Button>
							</div>
						)}

						{/* Action buttons */}
						{selectedFile ? (
							<div className="flex gap-2">
								<Button
									type="button"
									size="sm"
									className="flex-1"
									onClick={handleUpload}
									disabled={isUploading}
								>
									<Check className="mr-2 h-4 w-4" />
									{isUploading ? 'กำลังอัปโหลด...' : 'ยืนยันการอัปโหลด'}
								</Button>
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="flex-1"
									onClick={handleCancelSelection}
									disabled={isUploading}
								>
									<X className="mr-2 h-4 w-4" />
									ยกเลิก
								</Button>
							</div>
						) : (
							<div className="flex gap-2">
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="flex-1"
									onClick={handleUploadClick}
									disabled={isUploading}
								>
									<Upload className="mr-2 h-4 w-4" />
									อัปโหลดเสียงใหม่
								</Button>
								{currentSoundUrl && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={handleReset}
										disabled={isUploading}
									>
										<RotateCcw className="mr-2 h-4 w-4" />
										รีเซ็ตเป็นค่าเริ่มต้น
									</Button>
								)}
							</div>
						)}

						{/* Help text */}
						{!selectedFile && (
							<p className="text-xs text-muted-foreground">
								รองรับไฟล์ MP3 เท่านั้น ขนาดไม่เกิน 5MB
							</p>
						)}
					</>
				)}

				{/* Hidden file input */}
				<input
					ref={fileInputRef}
					type="file"
					accept=".mp3,audio/mpeg"
					onChange={handleFileSelect}
					className="hidden"
					disabled={isUploading}
				/>
			</CardContent>
		</Card>
	)
}
